
import { supabase } from '../../client';
import { CancelBookingResult } from './types';

export const cancelBooking = async (
  bookingId: string, 
  userId: string
): Promise<CancelBookingResult> => {
  try {
    // 1. First get the booking to check ownership and retrieve class ID
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('class_id, group_size, payment_status, booking_type, subscription_id, gift_card_id')
      .eq('id', bookingId)
      .eq('user_id', userId)
      .single();
    
    if (fetchError || !booking) {
      console.error('Error fetching booking:', fetchError);
      return { 
        success: false, 
        error: fetchError?.message || 'Booking not found or you are not authorized to cancel it' 
      };
    }
    
    // 2. Update the booking status in a transaction
    const { error: cancelError } = await supabase
      .from('bookings')
      .update({ status: 'cancelled' })
      .eq('id', bookingId)
      .eq('user_id', userId);
    
    if (cancelError) {
      console.error('Error cancelling booking:', cancelError);
      return { success: false, error: cancelError.message };
    }
    
    // 3. Update class available spots
    const { error: classError } = await supabase.rpc(
      'increment_class_spots',
      { class_id: booking.class_id, spots_to_add: booking.group_size || 1 }
    );
    
    if (classError) {
      console.error('Error updating class spots:', classError);
      return { success: false, error: classError.message };
    }
    
    // 4. If booking used a subscription, return the credit
    if (booking.booking_type === 'subscription' && booking.subscription_id) {
      const { error: subscriptionError } = await supabase.rpc(
        'return_subscription_credits',
        { 
          subscription_id: booking.subscription_id,
          credits_to_return: booking.group_size || 1
        }
      );
      
      if (subscriptionError) {
        console.error('Error returning subscription credits:', subscriptionError);
        // We don't fail the operation for this, but log the error
      }
    }
    
    // 5. If booking used a gift card, return the credit
    if (booking.booking_type === 'gift_card' && booking.gift_card_id) {
      const { error: giftCardError } = await supabase.rpc(
        'return_gift_card_credits',
        { 
          gift_card_id: booking.gift_card_id,
          credits_to_return: booking.group_size || 1
        }
      );
      
      if (giftCardError) {
        console.error('Error returning gift card credits:', giftCardError);
        // We don't fail the operation for this, but log the error
      }
    }
    
    // Return success
    return { success: true };
    
  } catch (error: any) {
    console.error('Error in cancelBooking:', error);
    return { success: false, error: error.message };
  }
};
